import React from "react";
import { formatDate } from "~/utils/functions/formatDate";
import { cn } from "~/lib/utils";
import { Tooltip } from "~/components/Tooltip";
import { faQuestionCircle } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ClassnamePassthruWrapper } from "~/components/ui/classname-passthru-wrapper";
import { ColumnSpanConfig } from "~/types";
import {
    ChartWatermark,
    getChartWatermarkClassName,
} from "./getChartWatermarkClassName";

// // // //
// Chart Export Notes (without using Highcharts Export module)

// function handleExportClick(props: { ref: any }) {
//     const { ref } = props;

//     // Get the chart instance
//     // const chart = Highcharts.chart("chart-container");
//     const opts: any = ref.current;
//     // console.log("HANDLE?");
//     // console.log(ref);
//     if (!opts.chart) {
//         // console.log(ref.current);
//         // ref.current.retry();
//         return;
//     }

//     console.log(opts.container);
//     console.log(opts.chart);

//     // Export the chart as PNG
//     // (opts.chart as Highcharts.Chart).exportChart(
//     // opts.chart.exportChart(
//     //     {
//     //         type: "image/png",
//     //         filename: "chart-" + Date.now(),
//     //         width: 800, // Specify the desired width of the exported image
//     //         chartOptions: {
//     //             title: {
//     //                 text: undefined,
//     //             },
//     //         },
//     //     },
//     //     (data: any) => {
//     //         // Handle the exported chart data
//     //         console.log("EXPORTED DATA");
//     //         console.log(data);

//     //         // Download the exported PNG file
//     //         // const link = document.createElement("a");
//     //         // link.href = data;
//     //         // link.download = "chart.png";
//     //         // link.click();
//     //         download(data);
//     //     },
//     // );

//     // Creating a new form data instance
//     const formData = new FormData();

//     formData.append("filename", "chart");

//     // TODO - test all form types
//     formData.append("type", "image/png"); // PNG
//     // formData.append("type", "image/svg+xml"); // SVG
//     // formData.append("type", "image/jpeg"); // JPG
//     // formData.append("type", "application/pdf"); // PDF
//     formData.append("width", "undefined"); // You may want to double-check if 'undefined' is intended as a string value
//     formData.append("scale", "2");

//     // NOTE - can probably customize the SVG a bit before exporting
//     // NOTE - we can also probably pre-render these SVGs and serve them via CDN, and load highcharts dynamically
//     // const mySvg = opts.chart.getSVG();
//     // formData.append("svg", mySvg);

//     if (!ref.current) {
//         return;
//     }

//     // THIS IS HOW YOU DO IT WITHOUT IMPORTING HIGHCHART-EXPORTS
//     console.log("ref.current");
//     // @ts-ignore
//     console.log(ref.current.container.current);
//     // @ts-ignore
//     console.log('ref.current.querySelector("svg")');
//     // @ts-ignore
//     console.log(ref.current.container.current.querySelector("svg"));
//     // @ts-ignore
//     const mySvg = ref.current.container.current.querySelector("svg");
//     // @ts-ignore
//     formData.append("svg", mySvg.outerHTML);

//     // @ts-ignore
//     // formData.append("svg", ref.current?.innerHTML);

//     // NOTE - can be done without including exports module
//     axios
//         .post("https://hammerhead-app-bhhew.ondigitalocean.app/", formData, {
//             headers: {
//                 "Content-Type": "multipart/form-data",
//             },
//             responseType: "blob", // This instructs axios to handle the response as a blob
//         })
//         .then((resp: any) => {
//             console.log("resp");
//             console.log(resp);
//             download(resp.data, "chart.png", "image/png");
//         });
// }

// // // //

function InfoTooltip(props: { content: React.ReactNode }) {
    const { content } = props;

    return (
        <div className="inline-block">
            <Tooltip
                placement="right"
                tooltipContent={
                    <p className="py-2 px-4 text-sm max-w-[20rem]">{content}</p>
                }
            >
                {({ setTriggerRef }) => (
                    <span ref={setTriggerRef}>
                        <FontAwesomeIcon
                            icon={faQuestionCircle}
                            className="text-slate-800/50 transition-colors h-4 cursor-help"
                        />
                    </span>
                )}
            </Tooltip>
        </div>
    );
}

// // // //

export const DEFAULT_COL_SPANS: ColumnSpanConfig = {
    sm: 12,
    md: 12,
    lg: 6,
    xl: 6,
    xxl: 6,
};

/**
 * VisualizationCard
 * Renders a styled card w/ title around a Visualization component
 */
export function VisualizationCard(props: {
    title: string;
    subtitle?: string;
    infoTooltip?: React.ReactNode;
    footer?: React.ReactNode;
    children: React.ReactNode | (() => React.ReactNode);
    watermark?: ChartWatermark;
    wrapperClassName?: string;
    wrapperClassNameOverride?: string;
    containerClassNameOverride?: string;
    colSpans?: ColumnSpanConfig;
    cardClassName?: string;
    cardStyle?: React.CSSProperties;
    showTimestamp?: boolean;
    customControls?: React.ReactNode;
    customControlBar?: React.ReactNode;
}) {
    const {
        title,
        subtitle = null,
        infoTooltip = null,
        footer = null,
        children,
        watermark = "bottom-right",
        wrapperClassName,
        wrapperClassNameOverride = "flex grow h-full items-center justify-center w-full",
        containerClassNameOverride = "flex flex-col",
        colSpans = DEFAULT_COL_SPANS,
        showTimestamp = true,
        customControls = null,
        customControlBar = null,
        cardClassName,
        cardStyle: style = {
            minHeight: "300px",
        },
    } = props;

    const smColSpan = colSpans.sm || DEFAULT_COL_SPANS.sm;
    const mdColSpan = colSpans.md || DEFAULT_COL_SPANS.md;
    const lgColSpan = colSpans.lg || DEFAULT_COL_SPANS.lg;
    const xlColSpan = colSpans.xl || DEFAULT_COL_SPANS.xl;
    // FIX: this is a fallback for colSpans.xxl to colSpans.xl to mimic mobile-first behavior
    // TODO - make all DEFAULT_COL_SPANS 12
    const xxlColSpan = colSpans.xxl || colSpans.xl || DEFAULT_COL_SPANS.xxl;

    // const ref = React.useRef(null);

    const watermarkCss = getChartWatermarkClassName({ watermark });

    return (
        <div
            className={cn(
                "p-5 bg-card rounded-lg shadow-xs border",
                "col-span-1 flex flex-col justify-between",
                `col-span-12 sm:col-span-${smColSpan} md:col-span-${mdColSpan} lg:col-span-${lgColSpan} xl:col-span-${xlColSpan} 2xl:col-span-${xxlColSpan}`,
                cardClassName,
            )}
            style={style}
        >
            <div className="flex flex-col items-start gap-2 mb-2 md:flex-row md:justify-between md:items-center md:gap-0 md:mb-0">
                <div className="w-full">
                    <ClassnamePassthruWrapper
                        className={cn({
                            "flex items-center gap-2": infoTooltip !== null,
                        })}
                    >
                        <p className="text-foreground text-xl leading select-none">
                            {title}
                        </p>

                        {infoTooltip && <InfoTooltip content={infoTooltip} />}
                    </ClassnamePassthruWrapper>

                    {subtitle !== null && (
                        <p className="body-04 leading-normal text-muted-foreground">
                            {subtitle}
                        </p>
                    )}
                </div>

                {customControls && <>{customControls}</>}
            </div>

            {customControlBar && <>{customControlBar}</>}

            <div className={containerClassNameOverride}>
                <div
                    className={cn(
                        watermarkCss,
                        wrapperClassName,
                        wrapperClassNameOverride,
                    )}
                >
                    {typeof children === "function" ? children() : children}
                </div>
                {footer}
                {showTimestamp && (
                    <div
                        className={cn(
                            "select-none flex text-xs justify-end text-muted-foreground font-light tracking-wide",
                            "-mb-2 pt-2",
                        )}
                    >
                        as of {formatDate(new Date())}
                    </div>
                )}
            </div>
        </div>
    );
}
